// components/source/sourceShortlistContext.ts
//
// THE SUBJECT OF A SOURCE TURN, when a shortlist is open or the conversation is bound to one.
//
// Two ways a turn gets a shortlist. The session's own subject can carry a campaign binding
// (`activeSubject.campaignId`, stamped at launch and on open) - that one wins, because the prompt
// must describe the campaign this conversation BELONGS to even if the user has since put another
// page on screen. Failing that, the globally open selection (`sourceSelectedCampaignId` under the
// asset scope) is used. Historically nothing re-stamped the subject when a shortlist opened, so
// the Dealer reasoned about the default portfolio while the user read one asset's list and
// answered "UPDATE Portfolio - ALTAREA" to a remark about the asset on screen; this module is the
// correction, handing the turn what is actually open, as the subject and as a readable block.
//
// BOTH granularities carry the EFFECTIVE rubric, read from the same endpoint the right bar reads
// (`/doctrine-context`), which derives it the way that run's own worker derives it: the asset's
// stored rubric plus the launch-brief criteria for an asset run, the asset's unit-fit rubric (or
// the standard grid it falls back to) for a unit one. That is the only source that describes what
// really scored the rows, and it is what makes the difference between the agent quoting the real
// criteria and inventing a tidy three-line rubric summing to 100.
//
// The unit branch used to read `Property.unitFitRubric` off the registry instead, because the
// endpoint answered 404 for a unit campaign. It no longer does, and reading the registry was wrong
// in a way that mattered: an asset with no saved unit-fit rubric is still scored, on the standard
// grid, and the block then told the agent the rubric was "not readable".
//
// PURE read + at most one GET. Never mutates, never navigates, never breaks a turn.
import { readCampaignRow } from '#lib/leasing/campaignRow';
import type { KernelHandle, SourcingCampaignDoctrineContextDTO } from '#types/types';
import { resolveOpenCampaignScope } from '#lib/leasing/source-view-guards';
import { resolveCampaignPropertyIds } from '#lib/leasing/sourcingCampaignLifecycle';
import { resolveSourceFocusedBrandId } from '#lib/leasing/sourceFocusedBrand';
import { buildSourceSlice } from './sourceTableSlice';

export interface OpenSourceShortlist {
  campaignId: string;
  /**
   * What the open run covered, read off the run itself. Only `asset` remains: it scored brands
   * against the whole property (Lead rows).
   */
  scope: 'asset';
  propertyId: string;
  propertyName: string;
  /**
   * EVERY asset the run covered, primary first. One entry for a single-asset run, so a reader that
   * only cares about the target can keep using `propertyId`; several when the user launched one
   * sourcing over several assets, which the prompt must say rather than naming the first.
   */
  coveredPropertyIds: string[];
  portfolioId: string;
  portfolioName: string;
  status: string;
  /**
   * Whether this shortlist is what the user has on SCREEN right now, as opposed to only being
   * the campaign this conversation is bound to. The prompt header must not claim "the user is
   * reading this right now" when they are looking at another page.
   */
  onScreen: boolean;
  /**
   * `id:weight` pairs of the rubric that governs this run, read live off the inventory registry.
   * It is the cache key for
   * the scoring fetch below: any edit, the agent's own approved proposal, the right-bar editor, a
   * socket update, changes it, so the loop's next pass can never be handed back the weights it
   * just replaced.
   */
  rubricSignature: string;
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const ids = (v: unknown): string[] => (Array.isArray(v) ? v.map(str).filter(Boolean) : []);

/** The campaign row as this module needs it, whichever registry actually holds it. */
interface CampaignRow {
  propertyId?: unknown;
  /** Carries `propertyIds` when the run covers several assets. */
  config?: unknown;
  propertyName?: unknown;
  status?: unknown;
}

/**
 * The shortlist the user has open in Source, or null. Same resolution rule as
 * `resolveShortlistTarget` in components/dealerCommands.ts: the run says itself, through its
 * own row, which granularity it covered. A PORTFOLIO run has no asset and is not a shortlist in
 * this sense, so it stays out.
 */
export function resolveOpenSourceShortlist(
  k: KernelHandle | null | undefined,
  /**
   * The campaign the TURN'S OWN session is bound to (its subject's `campaignId`), when it has
   * one. It outranks the globally open campaign: a background pass for campaign A must reason
   * about A's shortlist even if the user has since opened campaign B on screen.
   */
  sessionBoundCampaignId?: string | null,
): OpenSourceShortlist | null {
  if (!k) return null;
  try {
    const state = k.getState?.();
    if (!state) return null;
    const campaignId = str(sessionBoundCampaignId) || str(state.sourceSelectedCampaignId);
    if (!campaignId) return null;
    // No mode to consult: the campaign says itself, through its own row, what it covered. Asked
    // about the BOUND campaign rather than the open one, because a background pass must describe
    // the run its conversation belongs to even when another one is on screen.
    const scope = resolveOpenCampaignScope({ sourceSelectedCampaignId: campaignId, inventory: state.inventory });
    if (scope === 'portfolio') return null;

    const inv = k.getInventory();
    const campaign = inv?.SourcingCampaign?.registry?.[campaignId] as CampaignRow | undefined;
    // The unified registry is the fallback for a row hydrated by the campaign list route and
    // never entered in its legacy one; it names the target differently (arrays, one count pair).
    const unified = inv?.Campaign?.registry?.[campaignId] as
      | { propertyIds?: unknown; status?: unknown; targetName?: unknown }
      | undefined;
    if (!campaign && !unified) return null;

    const propertyId = str(campaign?.propertyId) || ids(unified?.propertyIds)[0] || '';
    if (!propertyId) return null;

    const property = inv?.Property?.registry?.[propertyId] as
      | { name?: unknown; portfolioId?: unknown; scoringRubric?: unknown }
      | undefined;
    const portfolioId = str(property?.portfolioId);
    const storedRubric = property?.scoringRubric;
    const rubricSignature = Array.isArray(storedRubric)
      ? (storedRubric as Array<{ id?: unknown; weight?: unknown }>)
          .map((criterion) => `${str(criterion?.id)}:${Number(criterion?.weight) || 0}`)
          .join('|')
      : '';
    const portfolio = portfolioId
      ? (inv?.Portfolio?.registry?.[portfolioId] as { name?: unknown } | undefined)
      : undefined;
    // Every asset the run covers, so the agent cannot describe a three-asset sourcing as if it were
    // about the one asset the campaign's column happens to name. Read off the run's own config; the
    // unified registry is the fallback for a row only the campaign list route ever hydrated.
    const declaredCoverage = resolveCampaignPropertyIds(campaign?.config, propertyId);
    const projectedCoverage = ids(unified?.propertyIds);
    const coveredPropertyIds =
      declaredCoverage.length > 1
        ? declaredCoverage
        : projectedCoverage.length > 1
          ? projectedCoverage
          : [propertyId];

    return {
      campaignId,
      scope,
      propertyId,
      propertyName:
        str(property?.name) || str(campaign?.propertyName) || str(unified?.targetName) || propertyId,
      coveredPropertyIds,
      portfolioId,
      portfolioName: str(portfolio?.name),
      status: str(campaign?.status) || str(unified?.status) || 'UNKNOWN',
      onScreen: campaignId === str(state.sourceSelectedCampaignId),
      rubricSignature,
    };
  } catch {
    return null;
  }
}

// One shortlist's scoring context is stable between edits, and the agentic loop takes several
// passes on one user message, so cache it rather than re-aggregate hundreds of rows per pass. The
// key carries everything that can invalidate it: the campaign, the live rubric signature, and
// `sourceDoctrineRefreshToken` (the counter UPDATE_SHORTLIST_DOCTRINE bumps). An edit the agent
// just made is therefore never served stale back to it on its own next pass.
const CONTEXT_TTL_MS = 20_000;
let cacheKey = '';
let cacheAt = 0;
let cacheValue: SourcingCampaignDoctrineContextDTO | null = null;

async function fetchScoringContext(
  campaignId: string,
  cacheDiscriminator: string,
): Promise<SourcingCampaignDoctrineContextDTO | null> {
  const key = `${campaignId}:${cacheDiscriminator}`;
  if (key === cacheKey && Date.now() - cacheAt < CONTEXT_TTL_MS) return cacheValue;
  try {
    const response = await fetch(
      `/api/inventory/sourcing-campaigns/${encodeURIComponent(campaignId)}/doctrine-context`,
      { cache: 'no-store' },
    );
    if (!response.ok) return null;
    const payload = (await response.json()) as SourcingCampaignDoctrineContextDTO;
    cacheKey = key;
    cacheAt = Date.now();
    cacheValue = payload;
    return payload;
  } catch {
    return null;
  }
}

/** How the run treats the brand data: research runs on every pass, this only says what it keeps. */
const researchModeLine = (config: Record<string, unknown> | null | undefined): string => {
  const legacy = config?.useLegacyData !== false;
  return `- DATA MODE: every brand of this pass is researched on the server before its one scoring ("Legacy data" ${legacy ? 'ON: fields checked in the last 30 days are kept' : 'OFF: every field is re-checked'}). Do not research brands for it, and give no verdict until the scoring lands.`;
};

/**
 * The readable block. Injected into the Dealer turn's `situation` (which the prompt template
 * renders as LIVE WORKSPACE CONTEXT), exactly the way the open Analyst table is.
 */
export async function buildSourceShortlistContext(
  k: KernelHandle | null | undefined,
  shortlist: OpenSourceShortlist,
): Promise<string> {
  const lang = k?.getState?.()?.language === 'fr' ? 'fr' : 'en';

  const header = [
    'SOURCE - OPEN SHORTLIST (this is the subject of this conversation).',
    // The claim about the user's screen has to be TRUE: a background pass can run for a bound
    // conversation while the user is reading another page, and telling the agent "the user is
    // reading this right now" in that state makes it confidently mis-describe the screen.
    ...(shortlist.onScreen
      ? [
          `The user is reading THIS asset shortlist on screen right now. It OUTRANKS every other scope in`,
          'your context, including the portfolio that happens to be the session default: when the user says',
          '"this asset", "this list", "the rubric", "the scoring", they mean the ones below. Never answer a',
          'remark about this list by proposing a change on its portfolio.',
        ]
      : [
          'This conversation BELONGS to the shortlist below; the user may currently be viewing another',
          'page. Reason and act on THIS shortlist - it outranks the session-default portfolio - but do',
          'not claim to know what is on the user\'s screen.',
        ]),
    `- Asset (Property): "${shortlist.propertyName}"  propertyId=${shortlist.propertyId}`,
    // A run can cover SEVERAL assets. Saying so is not decoration: the line above names one, and an
    // agent that took it for the whole scope would tell the user their sourcing was about that one
    // asset while its shortlist holds three assets' leads.
    shortlist.coveredPropertyIds.length > 1
      ? `- This ONE run covers ${shortlist.coveredPropertyIds.length} assets, analysed one after another: propertyIds=${shortlist.coveredPropertyIds.join(', ')}. ` +
        `The asset named above is only the first of them. Each was judged on ITS OWN rubric, so never describe "the rubric of this run" as a single document, ` +
        `and never answer a remark about this list as if it were about one asset.`
      : '',
    `- Shortlist (SourcingCampaign): campaignId=${shortlist.campaignId}  status=${shortlist.status}`,
    researchModeLine(readCampaignRow(k?.getState?.()?.inventory, shortlist.campaignId)?.config),
    shortlist.portfolioId
      ? `- Parent portfolio (context only, NOT the target): "${shortlist.portfolioName || 'unnamed'}" portfolioId=${shortlist.portfolioId}`
      : '',
    `- The user reads and writes in ${lang === 'fr' ? 'French' : 'English'}; the rubric itself is stored in French.`,
    ...(shortlist.coveredPropertyIds.length > 1
        ? [
            // A run over several assets was judged against several rubrics, one per asset, and only
            // the primary asset's is readable here. Saying so is the difference between the agent
            // quoting the wrong weights with confidence and telling the user what it does not know.
            'This run covers several assets, each weighed on its own: SET_RUN_WEIGHTS and REAPPLY_RUBRIC',
            'are refused on it. Say that plainly rather than proposing them.',
          ]
        : [
          `SET_RUN_WEIGHTS / REAPPLY_RUBRIC / READ_SHORTLIST_DOCTRINE / UPDATE_SHORTLIST_DOCTRINE target campaignId=${shortlist.campaignId}`,
          'and default to it when you omit the id.',
        ]),
  ]
    .filter(Boolean)
    .join('\n');

  const refreshToken = Number(k?.getState?.()?.sourceDoctrineRefreshToken ?? 0);
  const context = await fetchScoringContext(
    shortlist.campaignId,
    `${refreshToken}:${shortlist.rubricSignature}`,
  );

  if (!context || !Array.isArray(context.effectiveRubric) || context.effectiveRubric.length === 0) {
    return `${header}

SCORING GRID: could not be read this turn. Do NOT propose weights from memory; say plainly that you
could not read it.`;
  }

  const total = context.effectiveRubric.reduce((sum, row) => sum + (Number.isFinite(row.weight) ? row.weight : 0), 0);
  const format = (rows: typeof context.effectiveRubric): string =>
    rows
      .map(
        (criterion, index) =>
          `  ${index + 1}. "${criterion.label}"  id=${criterion.id}  weight=${criterion.weight}`,
      )
      .join('\n');

  const counts = context.counts ?? { rowsTotal: 0, rowsScored: 0, rowsSampled: 0 };

  // THE BRAND THE USER HAS OPEN, WITH ITS FIGURES.
  //
  // Without this the turn carried the shortlist and the ontology but no brand INSTANCE, so the
  // agent had the field NAMES and no values. Asked to prove the revenue that the panel was showing
  // beside it, it answered - correctly, for what it could see - that no revenue field was present
  // for this brand. Doctrine cannot fix that: the number genuinely was not there.
  //
  // Rendered with the same slice the chat's brand card uses, so what the agent reads and what the
  // user sees come from one definition and cannot drift.
  // THE ASSET'S FILTERS AS THEY STAND NOW, which is what the right bar shows.
  //
  // The brief further down is the CAMPAIGN's, frozen at launch. The two agree until someone edits
  // the asset - and then the panel says one thing and the agent reasons from another. Measured: the
  // minimum fit score and the two exclusion toggles were on screen and absent from the turn.
  const assetFiltersBlock = (() => {
    try {
      const inv = k?.getInventory?.();
      const property = inv?.Property?.registry?.[shortlist.propertyId] as
        | { sourcingCategoryFilters?: Record<string, unknown> | null; sourcingExclusionTenants?: unknown; sourcingExclusionRecentLeads?: unknown }
        | undefined;
      if (!property) return '';
      const f = (property.sourcingCategoryFilters ?? {}) as Record<string, unknown>;
      const parts: string[] = [];
      const surfaceMin = typeof f.surfaceMinSqm === 'number' ? f.surfaceMinSqm : null;
      const surfaceMax = typeof f.surfaceMaxSqm === 'number' ? f.surfaceMaxSqm : null;
      if (surfaceMin || surfaceMax) parts.push(`surface ${surfaceMin ?? 0}-${surfaceMax ?? '∞'} m2`);
      if (Array.isArray(f.acceptedFormats) && f.acceptedFormats.length > 0) parts.push(`format ${(f.acceptedFormats as unknown[]).map(str).filter(Boolean).join(', ')}`);
      if (Array.isArray(f.acceptedMarketPositionings) && f.acceptedMarketPositionings.length > 0) {
        parts.push(`positioning ${(f.acceptedMarketPositionings as unknown[]).map(str).filter(Boolean).join(', ')}`);
      }
      if (Array.isArray(f.acceptedCnccActivities) && f.acceptedCnccActivities.length > 0) {
        parts.push(`sectors ${(f.acceptedCnccActivities as unknown[]).map(str).filter(Boolean).join(', ')}`);
      }
      if (typeof property.sourcingExclusionTenants === 'boolean') {
        parts.push(`exclude on-site tenants ${property.sourcingExclusionTenants ? 'on' : 'off'}`);
      }
      if (typeof property.sourcingExclusionRecentLeads === 'boolean') {
        parts.push(`exclude recently scored ${property.sourcingExclusionRecentLeads ? 'on' : 'off'}`);
      }
      if (parts.length === 0) return '';
      return `

ASSET SOURCING FILTERS as stored on "${shortlist.propertyName}" RIGHT NOW, which is what the right
bar shows and what the NEXT launch will use: ${parts.join('; ')}.
The brief below is the campaign's, frozen when it ran; where the two differ, these are current.`;
    } catch {
      return '';
    }
  })();

  // The brand panel is emitted by the FOCUS block now (it is on screen whether or not an asset
  // shortlist is), so this one carries the shortlist's own material only.
  const focusedBrandBlock = '';

  return `${header}${assetFiltersBlock}${focusedBrandBlock}

THE SCORING GRID of this run (the one grid of every run, with this run's weights): ${context.effectiveRubric.length} criteria, ${total} points.
THIS IS THE REAL ONE, already read for you: there is nothing left to look up before you can reason about it or propose a weight change.
${format(context.effectiveRubric)}

Rows: ${counts.rowsScored} scored of ${counts.rowsTotal} (${counts.rowsSampled} sampled for the breakdown).

The criteria are fixed: never add, remove or rename one. A weight change is SET_RUN_WEIGHTS with the
ids above, keeping ${total} points in total; state old weight -> new weight against exactly these numbers.
"catchment_fit" is measured, not judged: weighing it changes how much it counts, nothing else.`;
}

/**
 * The brand whose panel is open in Source, exactly as the panel shows it.
 *
 * IT LIVED INSIDE THE SHORTLIST BLOCK, WHICH IS WHY IT KEPT GOING MISSING. That block is built
 * only when an ASSET shortlist is open, so on a portfolio-fit reference shortlist - and on any
 * session whose campaign has since been deleted - the agent was handed the brand's NAME and
 * nothing else, while the panel beside it displayed revenue, network size, category and the
 * expansion series. Measured, not reasoned: asked to prove the figures for an open brand, the
 * turn carried `- Brand: JD Sports (Brand id ...)` and one line more, and the agent hedged with
 * "if the UI is showing 4,850 stores" about a panel reading 488.
 *
 * What this block depends on is the brand being ON SCREEN, so that is what it is gated on now.
 * Resolution goes through `resolveSourceFocusedBrandId`, which knows both row kinds (PortfolioFit
 * and Lead); the path this used to take read the Lead registry alone and could not see a
 * portfolio-fit row at all.
 */
export async function buildFocusedBrandBlock(k: KernelHandle | null | undefined): Promise<string> {
  return (async () => {
    try {
      const state = k?.getState?.();
      const inv = k?.getInventory?.();
      if (!inv) return '';
      const brandId = resolveSourceFocusedBrandId(state as never);
      if (!brandId) return '';

      // HYDRATE, do not just read. This is the actual defect: the payload is assembled from the
      // CLIENT registries, and the brand behind a shortlist row is fetched lazily. Ask early in a
      // session and the row is not there yet, so the turn carried the ontology's field NAMES with
      // no values - which is exactly what the agent then reported, truthfully, as "no revenue
      // field is present for this brand" while the panel beside it displayed the revenue.
      if (!inv.Brand?.registry?.[brandId]) {
        await k?.ensureInventoryIds?.('Brand', [brandId]);
      }
      const brand = k?.getInventory?.()?.Brand?.registry?.[brandId];
      if (!brand) return '';

      // Same slice the chat's brand card uses, so what the agent reads and what the user sees come
      // from one definition and cannot drift apart.
      const slice = buildSourceSlice('brand', undefined, [brand]);
      if (!slice || slice.rows.length === 0) return '';
      const lines = slice.rows.map((row) => `- ${String(row[0])}: ${String(row[1])}`).join('\n');

      // THE EXPANSION PANEL, which lives behind its own endpoint and therefore never touched the
      // client registries at all - so no amount of hydration would have carried it. This is the
      // figure the very first "prove it" complaint was about (+37 openings, -24 closures): the
      // chart was on screen and the agent had nothing.
      const expansion = await (async () => {
        try {
          const res = await fetch(`/api/inventory/brands/${encodeURIComponent(brandId)}/openings`);
          if (!res.ok) return '';
          const o = (await res.json()) as Record<string, unknown>;
          // Only the authoritative series is stated as fact: the endpoint deliberately returns an
          // empty one otherwise, and presenting that as "+0 openings" is the lie it guards against.
          if (o.authoritative !== true) {
            const why = o.hasFrenchPresence === false ? 'operates outside France' : 'no INSEE establishment history linked';
            return `\n- Expansion: no authoritative series (${why}). Say that rather than quoting a zero.`;
          }
          const net = typeof o.netOpenings3y === 'number' ? o.netOpenings3y : null;
          const parts = [
            typeof o.openings3y === 'number' ? `${o.openings3y} openings` : '',
            typeof o.closures3y === 'number' ? `${o.closures3y} closures` : '',
            net !== null ? `net ${net > 0 ? '+' : ''}${net}` : '',
          ].filter(Boolean).join(', ');
          const meta = [
            o.status ? `status ${String(o.status)}` : '',
            typeof o.confidence === 'number' ? `confidence ${o.confidence}/100` : '',
            o.lastOpeningDate ? `last opening ${String(o.lastOpeningDate)}` : '',
            o.source ? `source ${String(o.source)}` : '',
          ].filter(Boolean).join(', ');
          return `\n- Expansion (3 years): ${parts || 'no movement recorded'}${meta ? ` (${meta})` : ''}`;
        } catch {
          return '';
        }
      })();

      // WHAT THIS BLOCK USED TO SAY, and why it is the most important edit in the change:
      // "you HAVE the figure: corroborate it with WEB_SEARCH rather than reporting it missing."
      // That is an instruction to defend whatever we stored. When the stored revenue turned out to
      // be an unrelated company's turnover, the agent was being told to go and find support for it.
      // An honest screen beside an agent under that instruction is worse than neither, because the
      // agent is the surface users actually ask.
      //
      // The panel now shows only proved values (lib/leasing/factGate.ts), so this block carries
      // only proved values too, and the instruction is inverted: say plainly what we cannot prove.
      return `

BRAND OPEN ON SCREEN, exactly as the panel shows it. Read the two kinds of value below differently:

MONEY (revenue, financial score, legal entity) has been through a verification gate. If a money
field appears here at all, it was proved against a named company and a source. If it is ABSENT,
we hold no proved figure - say so plainly and offer to research it with WEB_SEARCH. Never reach
for a remembered or stored number to fill that gap.

EVERYTHING ELSE (website, network size, positioning, category, countries, store cities, contacts,
logo) is an UNVERIFIED reading from our own database. It is context, not evidence. You may repeat
it, but say it is our stored value and not a checked fact, and offer to verify it if it matters.

${lines}${expansion}
Never present a figure you cannot attribute to a named company with a source. A figure attached to
the wrong company is the exact failure this rule exists to prevent: we once showed a brand the
turnover of an unrelated company, at "100% confidence", because nothing checked whose it was.`;
    } catch {
      return '';
    }
  })();
}
